import React from 'react';
import { View, Text, Pressable, StyleSheet, Platform } from 'react-native';
import { useTranslation } from 'react-i18next';
import { VerdictAvatars, type VerdictAvatarsVariant } from '../household/VerdictAvatars';

interface CookedStampProps {
  /** Recipe whose verdicts to show — the stamp reads the household, not a number. */
  recipeId: string;
  /** Image shape — controls border-radius of the dim overlay. */
  shape?: 'rect' | 'circle';
  /** Opens the verdict sheet. The row is a display, not an input. */
  onRate: () => void;
  /** Visual size variant — desktop is larger, mobile compact, dense is tiny. */
  size?: 'desktop' | 'mobile' | 'dense';
}

const VARIANT_FOR_SIZE: Record<
  NonNullable<CookedStampProps['size']>,
  VerdictAvatarsVariant
> = {
  desktop: 'card',
  mobile: 'mobile',
  dense: 'dense',
};

/**
 * "COOKED" stamp overlay that sits on top of a recipe image. Tilted ~6-8°
 * with the household's verdicts beneath it.
 *
 * This used to be a five-star row, which was wrong twice over: the stars were a
 * five-point display over a three-point input (solo, only 5/3/1 were reachable)
 * and they looked like a tappable rating while every tap just opened the sheet.
 * The pills show who actually answered; the whole overlay is the tap target.
 */
export const CookedStamp: React.FC<CookedStampProps> = ({
  recipeId,
  shape = 'rect',
  onRate,
  size = 'desktop',
}) => {
  const { t } = useTranslation();
  const isMobile = size === 'mobile';
  const isDense = size === 'dense';
  const tilt = isMobile ? '-8deg' : '-6deg';
  const borderRadius = shape === 'circle' ? 9999 : 0;

  return (
    <Pressable
      style={[styles.overlay, { borderRadius }]}
      onPress={(e: any) => {
        e?.stopPropagation?.();
        e?.preventDefault?.();
        onRate();
      }}
      accessibilityRole="button"
      accessibilityLabel={t('household.verdict.sectionLabel')}
      // Web-only: capture-phase stop so the ancestor card's onClick doesn't
      // fire and navigate to recipe detail mid-tap.
      {...(Platform.OS === 'web'
        ? ({
            onClickCapture: (e: any) => {
              e.stopPropagation();
              e.preventDefault();
            },
            onMouseDownCapture: (e: any) => e.stopPropagation(),
            onPointerDownCapture: (e: any) => e.stopPropagation(),
          } as any)
        : {})}
    >
      <View style={{ transform: [{ rotate: tilt }], alignItems: 'center' }}>
        <View style={[styles.mark, isMobile && styles.markMobile, isDense && styles.markDense]}>
          <Text
            style={[
              styles.markText,
              isMobile && styles.markTextMobile,
              isDense && styles.markTextDense,
            ]}
          >
            {t('common.cooked')}
          </Text>
        </View>
        <View style={styles.verdicts}>
          <VerdictAvatars
            recipeId={recipeId}
            variant={VARIANT_FOR_SIZE[size]}
            // A dense card has no room for the invitation, and the tiny type
            // would not be readable if it did.
            showCaption={!isDense}
          />
        </View>
      </View>
    </Pressable>
  );
};

const styles = StyleSheet.create({
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(20, 20, 20, 0.70)',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  mark: {
    borderWidth: 2,
    borderColor: '#fff',
    paddingVertical: 4,
    paddingHorizontal: 10,
    borderRadius: 3,
  },
  markMobile: {
    borderWidth: 2,
    paddingVertical: 4,
    paddingHorizontal: 10,
    borderRadius: 2,
  },
  markDense: {
    borderWidth: 1.5,
    paddingVertical: 2,
    paddingHorizontal: 5,
    borderRadius: 2,
  },
  markText: {
    color: '#fff',
    fontFamily: 'Inter-SemiBold',
    fontWeight: '700',
    letterSpacing: 3,
    fontSize: 11,
    ...Platform.select({
      web: { textShadow: '0 1px 2px rgba(0,0,0,0.4)' } as any,
    }),
  },
  markTextMobile: {
    fontSize: 12,
    letterSpacing: 2,
    ...Platform.select({
      web: { textShadow: '0 1px 3px rgba(0,0,0,0.8)' } as any,
    }),
  },
  markTextDense: {
    fontSize: 7,
    letterSpacing: 1.6,
  },
  verdicts: {
    marginTop: 6,
  },
});

export default CookedStamp;
